/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useMemo } from 'react';
import { ListItemText } from '@mui/material';
import Terminal from '../../shared/entities/terminal';
import { valueOrPlaceholder } from '../../utils/StringUtils';
import { formatTtho } from '../../utils/DateTimeUtils';
import {
  getBeamDisplayText,
  getNetworkAndBeamDisplayText,
  getSteerableBeamPriorityText,
} from '../../utils/TerminalListDrawerUtils';
import { terminalTypeDisplayNameFromTerminalTypeLabel } from '../../utils/TerminalUtilities';
import { isOnline } from '../../utils/ConnectionStatusUtils';

const PlatformListListItemDataDefault = (props: {
  terminal: Terminal;
  hideTtho: boolean;
  classes: any;
}) => {
  const { terminal, classes, hideTtho } = props;

  const leftListItemText = useMemo(
    () => (
      <ListItemText
        data-test="terminal-list-item-text__left-column"
        classes={{
          primary:
            terminal.connectivityStatus !== 'offline'
              ? terminal.connectivityStatus === 'active'
                ? classes.leftPrimaryActive
                : classes.leftPrimaryStandby
              : classes.leftPrimaryOffline,
          secondary: classes.leftSecondary,
          multiline: classes.leftMultiline,
        }}
        primary={
          <span data-test="terminal-list-item-text__left-column__terminal-status">
            {`${valueOrPlaceholder(
              terminalTypeDisplayNameFromTerminalTypeLabel(terminal.terminalTypeLabel),
            )} : ${getBeamDisplayText(terminal)}`}
          </span>
        }
        secondary={
          <span data-test="terminal-list-item-text__left-column__network-beam-status">
            {getNetworkAndBeamDisplayText(terminal)}
          </span>
        }
      />
    ),
    [terminal, classes],
  );

  const rightListItemText = useMemo(
    () =>
      isOnline(terminal.connectivityStatus) ? (
        <ListItemText
          data-test="terminal-list-item-text__right-column"
          classes={{
            primary: terminal.steerableBeamPriority ? classes.rightPrimary : classes.rightPrimaryHidden,
            secondary: classes.rightSecondary,
            multiline: classes.rightMultiline,
          }}
          primary={
            <span data-test="terminal-list-item-text__right-column__steerable-beam-priority">
              {getSteerableBeamPriorityText(terminal.steerableBeamPriority)}
            </span>
          }
          secondary={
            hideTtho ? (
              <></>
            ) : (
              <span data-test="terminal-list-item-text__right-column__ttho">{`TTHO : ${valueOrPlaceholder(
                terminal.tthoSeconds,
                formatTtho,
              )}`}</span>
            )
          }
        />
      ) : null,
    [terminal, classes, hideTtho],
  );

  return (
    <React.Fragment>
      {leftListItemText}
      {rightListItemText}
    </React.Fragment>
  );
};

export default PlatformListListItemDataDefault;
